import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import { usePolymorphicFlagKey } from '@gonfalon/router/usePolymorphicFlagKey';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import nullthrows from 'nullthrows';

import { fetchFlag as fetchFlagAction } from '#actions/flags';
import { FlagNotFound } from '#components/FlagNotFound';
import { AppSkeletonBody } from '#components/SkeletonLoader/AppSkeletonBody';
import { useManageFlagState } from '#components/useManageFlagState';
import { useDispatch } from '#hooks/useDispatch';
import { useSelector } from '#hooks/useSelector';
import { currentEnvironmentKeySelector } from '#reducers/projects';
import type { Flag } from '#utils/flags/records/Flag';
import type { Member as TMember } from '#utils/members/records/Member';

interface ManageFlagProps {
  envId: string;
  envKey: string;
  flag: Flag;
  flagKey: string;
  isABTestingEnabled: boolean;
  isCodeReferencesEnabled: boolean;
  isAuditLogEnabled: boolean;
  isFlagLinksEnabled: boolean;
  isInAppDemoEnabled: boolean;
  isWorkflowsTabEnabled: boolean;
  profile: TMember;
  projKey: string;
}

export function useRefreshLegacyFlag(projectKey: string, flagKey: string) {
  const fromUrl = useSelectedEnvironmentKey({ optional: true });
  const fromRedux = useSelector(currentEnvironmentKeySelector);
  const envKey = fromUrl || fromRedux || '';
  const dispatch = useDispatch();
  return () => {
    void dispatch(fetchFlagAction(projectKey, flagKey, envKey, { expandEvaluation: true })).catch(() => undefined);
  };
}

export function LegacyFlagAdapter({
  children,
  hideSkeleton,
}: {
  children: ((props: ManageFlagProps) => ReactNode) | ReactNode;
  hideSkeleton?: boolean;
}) {
  const projKey = useProjectKey();
  const fromUrl = useSelectedEnvironmentKey({ optional: true });
  const fromRedux = useSelector(currentEnvironmentKeySelector);
  const envKey = fromUrl || fromRedux;
  const { key: flagKey } = usePolymorphicFlagKey();
  const {
    isReady,
    error,
    flag: maybeFlag,
    fetchFlag,
    ...finalProps
  } = useManageFlagState({ projKey, envKey: envKey || '', flagKey: nullthrows(flagKey) });
  // `selected-env` in the URL; fall back to Redux. Strict `useSelectedEnvironmentKey()` `nullthrows`
  // and crashed deep-linked routes (e.g. `targeting/exclude-rules`) when search was not yet applied.

  useEffect(() => {
    if (!isReady) {
      void fetchFlag();
    }
  }, [fetchFlag, isReady]);

  if (!isReady) {
    // Some tabs don't need a skeleton loader
    return hideSkeleton ? null : <AppSkeletonBody />;
  }

  if (error) {
    return <FlagNotFound projKey={projKey} envKey={envKey} />;
  }

  const flag = nullthrows(maybeFlag, 'Expected flag');

  return typeof children === 'function' ? children({ ...finalProps, flag }) : children;
}
